import React, { useState, useEffect, useRef, useCallback } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Zoom from '@mui/material/Zoom';
import { toast } from 'react-toastify';
import { httpGet } from '../../HttpRequestHandling/httpRequestHandler';
import EnhancedDataGrid from '../Common/EnhancedDataGrid';
import IconButton from '@mui/material/IconButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Zoom in={true} ref={ref} {...props} />;
});

const ViewClientResourcesLookup = ({ openDialog, onClose, inProgress, clientCode, clientName, mediaType }) => {
    const [userRolesList, setUserRolesList] = useState([]);
    const [lastUpdated, setLastUpdated] = useState('');
    const [resources, setResources] = useState([]);
    const gridRef = useRef();

    useEffect(() => {
        if (openDialog) {
            setResources([]);
            setLastUpdated('');
            // Always empty the resource list when dialog opens
        }
    }, [openDialog]);

    const fetchResources = useCallback(() => {
        if (!mediaType || !clientCode) return;
        inProgress(true);
        httpGet(`/api/resources/clientmediatype?mediatypeid=${mediaType}&clientcode=${clientCode}`)
            .then((response) => {
                const resourceData = response.data?.resources || [];
                let rowCounter = 1;

                const resources = resourceData.flatMap((item) => {
                    const roles = item.apadClientUserRolesResource || [];

                    if (roles.length === 0) {
                        return [{
                            rowId: rowCounter++,
                            resourceId: item.resourceId,
                            resourceName: item.resourceName,
                            isActive: item.isActive,
                            userRoleId: null
                        }];
                    }

                    return roles.map(role => ({
                        rowId: rowCounter++,
                        resourceId: item.resourceId,
                        resourceName: item.resourceName,
                        isActive: item.isActive,
                        userRoleId: role.userRoleId
                    }));
                });
                setResources(resources);
                const serverDate = response.data?.lastUpdatedDate;
                setLastUpdated(serverDate ? new Date(serverDate).toLocaleString() : '');
                inProgress(false);
            })
            .catch(() => {
                toast.error('An error occurred while fetching the resources');
                inProgress(false);
            });
    }, [mediaType, clientCode, inProgress]);

    useEffect(() => {
        if (openDialog) {
            fetchResources();
        }
    }, [openDialog, clientCode, mediaType, fetchResources]);

    const getUserRoleName = useCallback((apadClientUserRolesResource) => {
        let assignedUserRolesResources = [];
        if (userRolesList.length > 0 && apadClientUserRolesResource && apadClientUserRolesResource.length > 0) {
            assignedUserRolesResources = apadClientUserRolesResource.map(role => {
                const userRole = userRolesList.find(ur => ur.userRoleId === role.userRoleId);
                return userRole ? userRole.userRoleName : '';
            }).filter(name => name);
            const joinedNames = assignedUserRolesResources.join(', ');
            if (joinedNames.length > 100) {
                return `${joinedNames.substring(0, 100)}...`;
            }
            return joinedNames;
        }
        return '';
    }, [userRolesList]);

    const getUserRoleNameList = useCallback((apadClientUserRolesResource) => {
        return (
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {(apadClientUserRolesResource || []).map((e, ei) => {
                    let rolename = userRolesList.find(ur => ur.userRoleId === e.userRoleId)?.userRoleName;
                    return (rolename && <li key={ei}>{rolename}</li>) || null;
                })}
            </ul>
        );
    }, [userRolesList]);

    useEffect(() => {
        if (openDialog) {
            inProgress(true);
            httpGet('/api/positioncategory/')
                .then((response) => {
                    const userRolesList = response.data.map((item) => ({
                        userRoleId: item.positionCategoryCode,
                        userRoleName: item.positionCategoryName
                    }));
                    setUserRolesList(userRolesList);
                    inProgress(false);
                })
                .catch(() => {
                    toast.error('An error occurred while fetching user roles');
                    inProgress(false);
                });
        }
    }, [openDialog, inProgress]);

    const columns = [
        //{
        //    headerName: 'Resource Code',
        //    field: 'resourceId',
        //    minWidth: 120,
        //    flex:0.5,
        //    renderCell: (props) => <span>{props.row.resourceId}</span>
        //},
        {
            headerName: 'Resource',
            field: 'resourceName',
            minWidth: 200,
            flex:1,
        },
        //{
        //    headerName: 'Active',
        //    field: 'isActive',
        //    type: 'boolean',
        //    width: 200,
        //    headerAlign: 'center',
        //    align: 'center',
        //    renderCell: (props) => (
        //        <input type="checkbox" checked={!!props.value} readOnly style={{ pointerEvents: 'none' }} />
        //    )
        //},
        {
            headerName: 'Category',
            field: 'userRoleId',
            minWidth: 250,
            flex: 1.5,
            renderCell: (params) => {
                const role = userRolesList.find(
                    r => String(r.userRoleId) === String(params.value)
                );

                const roleName = role ? role.userRoleName : '';

                return (
                    <Tooltip title={roleName}>
                        <Typography variant="inherit">{roleName}</Typography>
                    </Tooltip>
                );
            }
        }
    ];

    const handleClose = (event) => {
        if (onClose) {
            onClose(event);
        }
    };

    return (
        <Dialog open={openDialog} TransitionComponent={Transition} fullWidth maxWidth="lg">
             <DialogTitle>
              Client Resources
              <span style={{ marginLeft: 16, fontSize: '16px' }}>({clientName})</span>
            </DialogTitle>
            <DialogContent>
                <div style={{ display: 'block', overflow: 'auto', width: '100%' }}>
                    <EnhancedDataGrid
                        rows={resources}
                        columns={columns}
                        title={"Last Updated: " + lastUpdated}
                        getRowId={(row) => row.rowId}
                        pageSize={10}
                        pagination
                        gridRef={gridRef}
                        disableSelectionOnClick
                    />
                </div>
            </DialogContent>
            <DialogActions>
                <Button autoFocus onClick={handleClose} color="primary">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ViewClientResourcesLookup;